import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { appConfig } from '../config/AppConfig';

const DashboardContext = createContext();

const SYNC_INTERVAL = 300000; // 5 minutes (reduced to avoid slow API blocking UI)

export function DashboardProvider({ children }) {
  const [charts, setCharts] = useState(null);
  const [summaryKpis, setSummaryKpis] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRebuilding, setIsRebuilding] = useState(false);
  const [isIncrementalRefreshing, setIsIncrementalRefreshing] = useState(false);
  const [rebuildStatus, setRebuildStatus] = useState(null);
  const [summaryTablesAvailable, setSummaryTablesAvailable] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [scheduledJobs, setScheduledJobs] = useState(null);
  const [refreshOptions, setRefreshOptions] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const syncIntervalRef = useRef(null);
  const isInitialLoadRef = useRef(true);

  // Store current filter settings for syncing
  const currentFiltersRef = useRef({
    datePreset: 'all',
    startDate: null,
    endDate: null,
    topN: 10,
    language: 'ar'
  });

  const fetchDashboardData = useCallback(async (isBackground = false, filters = null) => {
    // Don't show loading spinner for background syncs
    if (!isBackground) {
      setLoading(true);
    } else {
      setIsSyncing(true);
    }

    // Use provided filters or current filters ref
    const filterParams = filters || currentFiltersRef.current;

    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();

      // Build URL with filter parameters
      const params = new URLSearchParams({
        db_id: 'sera_postgres'
      });

      if (filterParams.datePreset && filterParams.datePreset !== 'all') {
        params.append('date_preset', filterParams.datePreset);
      }

      if (filterParams.datePreset === 'custom' && filterParams.startDate && filterParams.endDate) {
        params.append('start_date', filterParams.startDate);
        params.append('end_date', filterParams.endDate);
      }

      if (filterParams.topN) {
        params.append('top_n', filterParams.topN.toString());
      }

      if (filterParams.language) {
        params.append('lang', filterParams.language);
      }

      const response = await fetch(`${apiUrl}/dashboard/charts?${params}`);

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to load dashboard data');
      }

      // Extract charts if available
      if (data.charts) {
        setCharts(data.charts);
      }

      // Extract summary KPIs and peak hours
      if (data.summary_kpis) {
        setSummaryKpis(data.summary_kpis);
      }
      if (data.peak_hours) {
        setPeakHours(data.peak_hours);
      }

      // Dashboard payload — generic (header meta + optional insight panels), from the backend.
      const apiStats = data;
      setDashboardData({
        meta:           apiStats.meta || null,
        summary:        apiStats.summary || {},
        grainComparison: apiStats.grain_comparison || null,
        drill:          apiStats.drill || {},
      });

      // Update summary tables status from API response
      setSummaryTablesAvailable(apiStats.summary_tables_available || false);
      if (apiStats.last_rebuild) {
        setRebuildStatus(apiStats.last_rebuild);
      }

      // Store applied filters from API response
      if (apiStats.applied_filters) {
        setAppliedFilters(apiStats.applied_filters);
      }

      setLastUpdated(new Date());
      setError(null);
      isInitialLoadRef.current = false;

    } catch (err) {
      console.error('Dashboard data fetch error:', err);
      // Only set error if this is initial load, not background sync
      if (!isBackground) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
      setIsSyncing(false);
    }
  }, []);

  // Fetch with specific filters (used when user changes settings)
  const fetchWithFilters = useCallback(async (filters) => {
    // Update the ref so background syncs use the same filters
    currentFiltersRef.current = {
      ...currentFiltersRef.current,
      ...filters
    };
    // Fetch with new filters
    await fetchDashboardData(false, currentFiltersRef.current);
  }, [fetchDashboardData]);

  // Initial load on app start
  useEffect(() => {
    fetchDashboardData(false);
  }, [fetchDashboardData]);

  // Background sync every minute
  useEffect(() => {
    syncIntervalRef.current = setInterval(() => {
      fetchDashboardData(true);
    }, SYNC_INTERVAL);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [fetchDashboardData]);

  const refresh = useCallback(() => {
    fetchDashboardData(false);
  }, [fetchDashboardData]);

  // Trigger full rebuild of summary tables
  const triggerRebuild = useCallback(async () => {
    setIsRebuilding(true);
    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();
      const response = await fetch(`${apiUrl}/dashboard/rebuild?triggered_by=manual`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`Rebuild request failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to rebuild summary tables');
      }

      // Refresh dashboard data after successful rebuild
      await fetchDashboardData(false);

      return { success: true, message: 'Summary tables rebuilt successfully' };
    } catch (err) {
      console.error('Rebuild error:', err);
      return { success: false, error: err.message };
    } finally {
      setIsRebuilding(false);
    }
  }, [fetchDashboardData]);

  // Trigger incremental refresh (~5 seconds)
  const triggerIncrementalRefresh = useCallback(async () => {
    setIsIncrementalRefreshing(true);
    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();
      const response = await fetch(`${apiUrl}/dashboard/refresh-incremental`, {
        method: 'POST'
      });

      if (!response.ok) {
        throw new Error(`Incremental refresh failed: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to run incremental refresh');
      }

      // Refresh dashboard data after successful refresh
      await fetchDashboardData(false);
      // Also refresh the scheduled jobs info
      await fetchScheduledJobs();

      return { success: true, message: 'Incremental refresh completed', duration_ms: result.duration_ms };
    } catch (err) {
      console.error('Incremental refresh error:', err);
      return { success: false, error: err.message };
    } finally {
      setIsIncrementalRefreshing(false);
    }
  }, [fetchDashboardData]);

  // Fetch scheduled jobs and refresh options
  const fetchScheduledJobs = useCallback(async () => {
    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();

      // Fetch both scheduled jobs and refresh options in parallel
      const [jobsResponse, optionsResponse] = await Promise.all([
        fetch(`${apiUrl}/dashboard/scheduled-jobs`),
        fetch(`${apiUrl}/dashboard/refresh-options`)
      ]);

      if (jobsResponse.ok) {
        const jobsData = await jobsResponse.json();
        setScheduledJobs(jobsData.jobs || null);
      }

      if (optionsResponse.ok) {
        const optionsData = await optionsResponse.json();
        setRefreshOptions(optionsData);
      }
    } catch (err) {
      console.error('Failed to fetch scheduled jobs:', err);
    }
  }, []);

  // Fetch scheduled jobs on mount
  useEffect(() => {
    fetchScheduledJobs();
  }, [fetchScheduledJobs]);

  const value = {
    charts,
    summaryKpis,
    peakHours,
    loading,
    error,
    lastUpdated,
    isSyncing,
    isInitialLoad: isInitialLoadRef.current,
    refresh,
    // Filter functionality
    fetchWithFilters,
    appliedFilters,
    // Rebuild functionality
    isRebuilding,
    rebuildStatus,
    summaryTablesAvailable,
    triggerRebuild,
    // Incremental refresh functionality
    isIncrementalRefreshing,
    triggerIncrementalRefresh,
    scheduledJobs,
    refreshOptions,
    fetchScheduledJobs,
    // SERA-specific
    dashboardData,
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}

export default useDashboard;
